// HKDSE 2022 Paper 1 Knowledge Graph Samples - Part 2
// Additional questions from the complete 2022 paper

export const hkdse2022Samples_part2 = {
  geometry_basic: [
    {
      id: 'hkdse2022_geometry_01',
      problem: "點 $S$ 及點 $T$ 的坐標分別為 $(12, -5)$ 及 $(-3, -7)$。$S$ 繞 $O$ 逆時針方向旋轉 $90°$ 至 $S'$，其中 $O$ 為原點。$T'$ 為 $T$ 對 $x$ 軸的反射影像。(a) 寫出 $S'$ 及 $T'$ 的坐標。(b) 求 $S'T'$ 的斜率。",
      problemType: "geometry_transformations",
      difficulty: "intermediate",
      description: "Coordinate geometry with rotations and reflections",
      graphData: {
        nodes: [
          {
            id: "rotation_90_degrees",
            name: "Rotation 90° Counterclockwise",
            type: "concepts",
            description: "Rotation formula: (x,y) → (-y,x)",
            value: "S(12,-5) → S'(-(-5),12) = S'(5,12)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "reflection_x_axis",
            name: "Reflection over X-axis",
            type: "concepts",
            description: "Reflection formula: (x,y) → (x,-y)",
            value: "T(-3,-7) → T'(-3,7)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "part_a_coordinates",
            name: "Part (a): Coordinates",
            type: "steps",
            description: "Calculate S' and T' coordinates",
            value: "S'(5,12), T'(-3,7)",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "slope_formula",
            name: "Slope Formula",
            type: "concepts",
            description: "Slope = (y₂-y₁)/(x₂-x₁)",
            value: "m = (y₂-y₁)/(x₂-x₁)",
            importance: 8,
            x: 0, y: 0, z: 25
          },
          {
            id: "part_b_slope_calculation",
            name: "Part (b): Slope Calculation",
            type: "steps",
            description: "Calculate slope of S'T'",
            value: "m = (7-12)/(-3-5) = -5/-8 = 5/8",
            importance: 9,
            x: 0, y: 100, z: 0
          },
          {
            id: "final_answers",
            name: "Final Answers",
            type: "values",
            description: "Coordinates and slope",
            value: "(a) S'(5,12), T'(-3,7) (b) 5/8",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "rotation_90_degrees", to: "part_a_coordinates", type: "enables", strength: 10 },
          { from: "reflection_x_axis", to: "part_a_coordinates", type: "enables", strength: 9 },
          { from: "part_a_coordinates", to: "slope_formula", type: "enables", strength: 8 },
          { from: "slope_formula", to: "part_b_slope_calculation", type: "enables", strength: 9 },
          { from: "part_a_coordinates", to: "final_answers", type: "contributes to", strength: 9 },
          { from: "part_b_slope_calculation", to: "final_answers", type: "contributes to", strength: 9 }
        ]
      }
    },
    {
      id: 'hkdse2022_geometry_02',
      problem: "圖1中，$A$ 是位於四邊形 $BCDE$ 以內的一點使得 $AC \\parallel ED$ 及 $AD \\parallel BC$。已知 $\\angle ABC = \\angle AED$ 及 $AB = AE$。(a) 證明 $\\triangle ABC \\cong \\triangle AED$。(b) 若 $\\angle ABC = 39°$ 及 $\\angle DAE = 87°$，求 $\\angle ACD$。",
      problemType: "geometry_congruence",
      difficulty: "intermediate",
      description: "Geometric proof and angle calculation",
      graphData: {
        nodes: [
          {
            id: "parallel_lines",
            name: "Parallel Lines",
            type: "concepts",
            description: "AC || ED and AD || BC",
            value: "Given parallel relationships",
            importance: 9,
            x: 0, y: -200, z: 0
          },
          {
            id: "given_conditions",
            name: "Given Conditions",
            type: "concepts",
            description: "∠ABC = ∠AED and AB = AE",
            value: "Equal angles and equal sides",
            importance: 10,
            x: -150, y: -100, z: 20
          },
          {
            id: "part_a_proof_setup",
            name: "Part (a): Proof Setup",
            type: "steps",
            description: "Identify congruent triangles",
            value: "△ABC and △AED have equal angles and sides",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "part_a_congruence_criteria",
            name: "Part (a): Congruence Criteria",
            type: "steps",
            description: "Use ASA or SAS criteria",
            value: "ASA: ∠ABC = ∠AED, AB = AE, ∠BAC = ∠EAD",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "part_a_conclusion",
            name: "Part (a): Conclusion",
            type: "steps",
            description: "Triangles are congruent",
            value: "△ABC ≅ △AED by ASA",
            importance: 10,
            x: 0, y: 100, z: 0
          },
          {
            id: "part_b_angle_relationships",
            name: "Part (b): Angle Relationships",
            type: "steps",
            description: "Use congruent triangles and angle sum",
            value: "∠ACD = 180° - ∠ABC - ∠DAE",
            importance: 8,
            x: 0, y: 200, z: 0
          },
          {
            id: "part_b_calculation",
            name: "Part (b): Calculation",
            type: "steps",
            description: "Calculate ∠ACD",
            value: "∠ACD = 180° - 39° - 87° = 54°",
            importance: 9,
            x: 0, y: 300, z: 0
          },
          {
            id: "final_answers",
            name: "Final Answers",
            type: "values",
            description: "Proof and angle value",
            value: "(a) △ABC ≅ △AED by ASA (b) 54°",
            importance: 10,
            x: 0, y: 400, z: 0
          }
        ],
        links: [
          { from: "parallel_lines", to: "part_a_proof_setup", type: "enables", strength: 9 },
          { from: "given_conditions", to: "part_a_proof_setup", type: "enables", strength: 10 },
          { from: "part_a_proof_setup", to: "part_a_congruence_criteria", type: "leads to", strength: 9 },
          { from: "part_a_congruence_criteria", to: "part_a_conclusion", type: "leads to", strength: 10 },
          { from: "part_a_conclusion", to: "part_b_angle_relationships", type: "enables", strength: 8 },
          { from: "part_b_angle_relationships", to: "part_b_calculation", type: "leads to", strength: 9 },
          { from: "part_a_conclusion", to: "final_answers", type: "contributes to", strength: 10 },
          { from: "part_b_calculation", to: "final_answers", type: "contributes to", strength: 9 }
        ]
      }
    }
  ],
  
  statistics_basic: [
    {
      id: 'hkdse2022_statistics_01',
      problem: "下面的頻數分佈表及累積頻數分佈表均顯示某群學生完成某 $3$ km 賽跑所需時間的分佈。(a) 寫出 $x$ 的值。(b) 求該分佈的平均值。(c) 求從該群中隨機選出的一名學生完成該 $3$ km 賽跑所需時間少於 $19.5$ 分鐘的概率。",
      problemType: "statistics_distribution",
      difficulty: "intermediate",
      description: "Statistical analysis with frequency tables",
      graphData: {
        nodes: [
          {
            id: "frequency_table",
            name: "Frequency Table",
            type: "concepts",
            description: "Time intervals and frequencies",
            value: "10-14: a, 15-19: 9, 20-24: b, 25-29: 3",
            importance: 9,
            x: 0, y: -200, z: 0
          },
          {
            id: "cumulative_table",
            name: "Cumulative Table",
            type: "concepts",
            description: "Cumulative frequency distribution",
            value: "<14.5: 3, <19.5: x, <24.5: y, <29.5: 20",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "part_a_find_x",
            name: "Part (a): Find x",
            type: "steps",
            description: "Use cumulative frequency relationship",
            value: "x = 3 + a + 9 = 12 + a",
            importance: 8,
            x: 150, y: -100, z: 20
          },
          {
            id: "part_a_solve_a",
            name: "Part (a): Solve for a",
            type: "steps",
            description: "Use total frequency = 20",
            value: "a + 9 + b + 3 = 20 → a + b = 8",
            importance: 8,
            x: 0, y: 0, z: 25
          },
          {
            id: "part_a_find_b",
            name: "Part (a): Find b",
            type: "steps",
            description: "Use cumulative frequency",
            value: "y = x + b = 12 + a + b = 20 → a = 4, b = 4",
            importance: 9,
            x: 0, y: 100, z: 0
          },
          {
            id: "part_a_x_value",
            name: "Part (a): x value",
            type: "steps",
            description: "Calculate x",
            value: "x = 12 + 4 = 16",
            importance: 9,
            x: 0, y: 200, z: 0
          },
          {
            id: "part_b_mean_calculation",
            name: "Part (b): Mean Calculation",
            type: "steps",
            description: "Calculate mean using midpoints",
            value: "Mean = (12×4 + 17×9 + 22×4 + 27×3)/20",
            importance: 8,
            x: -200, y: 150, z: 0
          },
          {
            id: "part_b_mean_result",
            name: "Part (b): Mean Result",
            type: "steps",
            description: "Calculate the mean",
            value: "Mean = (48 + 153 + 88 + 81)/20 = 18.5",
            importance: 9,
            x: -200, y: 200, z: 0
          },
          {
            id: "part_c_probability",
            name: "Part (c): Probability",
            type: "steps",
            description: "Find probability of time < 19.5 minutes",
            value: "P(<19.5) = (4 + 9)/20 = 13/20",
            importance: 8,
            x: 200, y: 150, z: 0
          },
          {
            id: "final_answers",
            name: "Final Answers",
            type: "values",
            description: "All required answers",
            value: "(a) 16 (b) 18.5 (c) 13/20",
            importance: 10,
            x: 0, y: 300, z: 0
          }
        ],
        links: [
          { from: "frequency_table", to: "part_a_find_x", type: "enables", strength: 9 },
          { from: "cumulative_table", to: "part_a_find_x", type: "enables", strength: 9 },
          { from: "part_a_find_x", to: "part_a_solve_a", type: "enables", strength: 8 },
          { from: "part_a_solve_a", to: "part_a_find_b", type: "leads to", strength: 9 },
          { from: "part_a_find_b", to: "part_a_x_value", type: "leads to", strength: 9 },
          { from: "part_a_x_value", to: "part_b_mean_calculation", type: "enables", strength: 8 },
          { from: "part_b_mean_calculation", to: "part_b_mean_result", type: "leads to", strength: 9 },
          { from: "part_a_x_value", to: "part_c_probability", type: "enables", strength: 8 },
          { from: "part_a_x_value", to: "final_answers", type: "contributes to", strength: 9 },
          { from: "part_b_mean_result", to: "final_answers", type: "contributes to", strength: 9 },
          { from: "part_c_probability", to: "final_answers", type: "contributes to", strength: 8 }
        ]
      }
    }
  ],
  
  functions_basic: [
    {
      id: 'hkdse2022_functions_01',
      problem: "已知 $f(x)$ 的一部分隨 $x^2$ 正變，而另一部分則隨 $x$ 正變。假定 $f(4) = 96$ 及 $f(-5) = 15$。(a) 求 $f(x)$。(b) 寫出 $y = 8f(x)$ 的圖像的 $x$ 截距。(c) 設 $k$ 為一實常數。求 $k$ 值的範圍使得方程 $f(x) = k$ 有兩個相異的實根。",
      problemType: "functions_variation",
      difficulty: "intermediate",
      description: "Function with direct variation and quadratic roots",
      graphData: {
        nodes: [
          {
            id: "function_structure",
            name: "Function Structure",
            type: "concepts",
            description: "f(x) has parts varying with x² and x",
            value: "f(x) = ax² + bx",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "given_conditions",
            name: "Given Conditions",
            type: "concepts",
            description: "f(4) = 96 and f(-5) = 15",
            value: "Two points on the function",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "step1_setup_equations",
            name: "Step 1: Setup Equations",
            type: "steps",
            description: "Substitute given points",
            value: "16a + 4b = 96, 25a - 5b = 15",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "step2_solve_system",
            name: "Step 2: Solve System",
            type: "steps",
            description: "Solve the system of equations",
            value: "a = 3, b = 12",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_write_function",
            name: "Step 3: Write Function",
            type: "steps",
            description: "Write the complete function",
            value: "f(x) = 3x² + 12x",
            importance: 9,
            x: 0, y: 100, z: 0
          },
          {
            id: "part_b_x_intercept",
            name: "Part (b): X-Intercept",
            type: "steps",
            description: "Find x-intercept of y = 8f(x)",
            value: "8f(x) = 0 → f(x) = 0 → x = 0 or x = -4",
            importance: 8,
            x: 0, y: 200, z: 0
          },
          {
            id: "part_c_quadratic_roots",
            name: "Part (c): Quadratic Roots",
            type: "steps",
            description: "Find range for two distinct real roots",
            value: "Discriminant > 0: 144 - 12k > 0",
            importance: 9,
            x: 0, y: 300, z: 0
          },
          {
            id: "part_c_solve_k",
            name: "Part (c): Solve for k",
            type: "steps",
            description: "Find range of k values",
            value: "k < 12",
            importance: 9,
            x: 0, y: 400, z: 0
          },
          {
            id: "final_answers",
            name: "Final Answers",
            type: "values",
            description: "All required answers",
            value: "(a) f(x) = 3x² + 12x (b) x = 0, -4 (c) k < 12",
            importance: 10,
            x: 0, y: 500, z: 0
          }
        ],
        links: [
          { from: "function_structure", to: "step1_setup_equations", type: "enables", strength: 10 },
          { from: "given_conditions", to: "step1_setup_equations", type: "enables", strength: 9 },
          { from: "step1_setup_equations", to: "step2_solve_system", type: "leads to", strength: 9 },
          { from: "step2_solve_system", to: "step3_write_function", type: "leads to", strength: 9 },
          { from: "step3_write_function", to: "part_b_x_intercept", type: "enables", strength: 8 },
          { from: "step3_write_function", to: "part_c_quadratic_roots", type: "enables", strength: 9 },
          { from: "part_c_quadratic_roots", to: "part_c_solve_k", type: "leads to", strength: 9 },
          { from: "step3_write_function", to: "final_answers", type: "contributes to", strength: 9 },
          { from: "part_b_x_intercept", to: "final_answers", type: "contributes to", strength: 8 },
          { from: "part_c_solve_k", to: "final_answers", type: "contributes to", strength: 9 }
        ]
      }
    }
  ]
}; 